<script setup lang="ts">
// MDM imports
import "@maersk-global/mds-components-core-tab";
import "@maersk-global/mds-components-core-tab-bar";
// Vue imports
import { onMounted, ref } from "vue";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useImportLocationStore } from "@/store/import/import_location_store";
import { useExportLocationStore } from "@/store/export/export_location_store";
import { useEquipmentLocationStore } from "@/store/history/equipment_location_store";
import { useScheduleLocationStore } from "@/store/schedule/schedule_location_store";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import ImportPageView from "@/components/import/ImportPageView.vue";
import ExportPageView from "@/components/export/ExportPageView.vue";
import VesselSchedulePageView from "@/components/schedule/VesselSchedulePageView.vue";
import EquipmentHistoryPageView from "@/components/history/EquipmentHistoryPageView.vue";
import { useGlobalStore } from "@/store/global";
import Loader from "@/components/Loader.vue";

const searchStore = useSearchIdsStore();
const importLocationStore = useImportLocationStore();
const exportLocationStore = useExportLocationStore();
const equipmentLocationStore = useEquipmentLocationStore();
const scheduleLocationStore = useScheduleLocationStore();
const terminalConfigurationStore = useTerminalConfigurationStore();
const globalStore = useGlobalStore();

const defaultIndex = ref<number>(0);
const defaultTerminal = ref<string>("");
const isLoading = ref<boolean>(false);

const props = defineProps({
  journey: {
    type: String,
    default: "import"
  }
});

onMounted((): void => {
  const path = window.location.href;
  const params = new URLSearchParams(new URL(path).search);
  const terminal = params.get("terminal")?.trim() || "";
  const journey = params.get("journey")?.trim() || props.journey.trim();

  defaultTerminal.value = terminal;

  if (journey !== "") {
    defaultIndex.value = getJourney(journey);
    tabChanges({
      detail: defaultIndex.value
    });
  }
});

const labels = {
  appName: globalStore.sitecoreData.headings.app_name,
  importPickup: globalStore.sitecoreData.appointment_types.import_pickup,
  exportDropOff: globalStore.sitecoreData.appointment_types.export_drop_off,
  containerEventHistory: globalStore.sitecoreData.appointment_types.container_event_history,
  vesselSchedule: globalStore.sitecoreData.appointment_types.vessel_schedule,
  loadingApp: globalStore.sitecoreData.common.loading_app
};

const getJourney = (journeyName: string): number => {
  switch (journeyName) {
    case "import":
      return 0;
    case "export":
      return 1;
    case "history":
      return 2;
    case "schedule":
      return 3;
    default:
      return 0;
  }
};

const tabChanges = (e: any): void => {
  isLoading.value = true;
  const tab = e.detail;
  if (tab == 0) {
    searchStore.setTransactionType("PUI");
    importLocationStore.fetchImportLocations();
    terminalConfigurationStore.setSearchIdLimit();
    if (defaultTerminal.value !== "") {
      importLocationStore.setTempLocation({ id: defaultTerminal.value });
    }
  } else if (tab == 1) {
    searchStore.setTransactionType("DOE");
    exportLocationStore.fetchExportLocations();
    terminalConfigurationStore.setSearchIdLimit();
    if (defaultTerminal.value !== "") {
      exportLocationStore.setTempLocation({ id: defaultTerminal.value });
    }
  } else if (tab == 2) {
    searchStore.setTransactionType("CEH");
    equipmentLocationStore.fetchEquipmentLocations();
    terminalConfigurationStore.setSearchIdLimit();
    if (defaultTerminal.value !== "") {
      equipmentLocationStore.setTempLocation({ id: defaultTerminal.value });
    }
  } else if (tab == 3) {
    searchStore.setTransactionType("VS");
    scheduleLocationStore.fetchScheduleLocations();
    if (defaultTerminal.value !== "") {
      scheduleLocationStore.setTerminal({ id: defaultTerminal.value });
    }
  }
  isLoading.value = false;
};
</script>

<template>
  <div class="app-container">
    <div class="app-container__home">
      <div class="page-header">
        <h2>{{ labels.appName }}</h2>
      </div>
      <div v-if="!isLoading">
        <mc-tab-bar fit="medium" :currentindex="defaultIndex" @tabchange="tabChanges($event)">
          <!-- tab 1: Import Availability-->
          <mc-tab slot="tab" :label="labels.importPickup" icon="container-up" class="search-tab"></mc-tab>
          <span slot="panel" class="search-tab-panel"><ImportPageView /></span>
          <!-- tab 2: Export Booking Enquiry-->
          <mc-tab slot="tab" :label="labels.exportDropOff" icon="container-down" class="search-tab"></mc-tab>
          <div slot="panel" class="search-tab-panel"><ExportPageView /></div>
          <!-- tab 3: Equipment History-->
          <mc-tab
            slot="tab"
            :label="labels.containerEventHistory"
            icon="container-tracking"
            class="search-tab"
          ></mc-tab>
          <div slot="panel" class="search-tab-panel"><EquipmentHistoryPageView /></div>
          <!-- tab 4: Vessel Schedule-->
          <mc-tab slot="tab" :label="labels.vesselSchedule" icon="vessel-front" class="search-tab"></mc-tab>
          <div slot="panel" class="search-tab-panel"><VesselSchedulePageView /></div>
        </mc-tab-bar>
      </div>
      <div v-else>
        <Loader :isLoading="isLoading" :loadingMessage="labels.loadingApp" />
      </div>

      <div class="button-top-margin-15px"></div>
    </div>
  </div>
</template>

<style>
.search-tab {
  background-color: white;
}
.search-tab-panel {
  padding: 0;
}
</style>
